import { Clock, Zap } from "lucide-react";

import { Badge } from "@/app/components/Badge/Badge";
import { Button } from "@/app/components/ui/button";
import { cn } from "@/app/handlers/cn.utils";
import { formatRelativeTime } from "@/app/handlers/utilityFunctions";

import { AssessedItem, AssessmentRecord, FitAssessment } from "../config/api";
import { isReadyToApply, requiredStats } from "../config/stats";

interface FitCheckCardProps {
	record: AssessmentRecord;
	onViewDetails: () => void;
}

const CATEGORY_LABELS: { key: keyof FitAssessment; label: string }[] = [
	{ key: "experience", label: "Experience" },
	{ key: "skills", label: "Skills" },
	{ key: "education", label: "Education" },
	{ key: "certifications", label: "Certifications" },
];

function categoryCount(items: AssessedItem[]) {
	const required = items.filter((i) => i.necessity === "required");
	if (required.length > 0) {
		return { num: required.filter((i) => i.status === "Fit").length, den: required.length };
	}
	return { num: items.filter((i) => i.status === "Fit").length, den: items.length };
}

export const FitCheckCard = ({ record, onViewDetails }: FitCheckCardProps) => {
	const ready = isReadyToApply(record.assessment);
	const { met, total } = requiredStats(record.assessment);

	return (
		<div className="bg-white rounded-lg border border-gray-200 p-6">
			<div className="flex items-center justify-between mb-3">
				<Badge
					className={cn(
						"text-xs font-medium rounded-xs flex items-center gap-1 border-0",
						ready ? "bg-green-100 text-green-700 hover:bg-green-100" : "bg-blue-100 text-blue-600 hover:bg-blue-100",
					)}
				>
					<Zap className="w-3 h-3" />
					{ready ? "Ready to Apply" : "Prepare First"}
				</Badge>
				<span className="text-xs text-gray-500">
					<span className="font-semibold tabular-nums text-gray-900">
						{met} of {total}
					</span>{" "}
					must-haves
				</span>
			</div>

			<div className="mb-4">
				<h3 className="font-bold! font-plus-jakarta! text-base text-gray-900">
					{record.roleTitle || "Unknown Position"}
				</h3>
				<p className="text-sm font-plus-jakarta text-gray-700">{record.company || "Unknown Company"}</p>
			</div>

			<div className="flex flex-wrap gap-2 mb-5">
				{CATEGORY_LABELS.map(({ key, label }) => {
					const items = record.assessment[key].items;
					if (items.length === 0) return null;
					const { num, den } = categoryCount(items);
					return (
						<span
							key={key}
							className="inline-flex items-center gap-1 rounded border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-700"
						>
							{label}{" "}
							<span className="font-semibold tabular-nums text-gray-900">
								{num}/{den}
							</span>
						</span>
					);
				})}
			</div>

			<div className="flex items-center justify-between pt-4 border-t border-gray-100">
				<div className="flex items-center gap-1 text-xs text-gray-400">
					<Clock className="w-3 h-3" />
					<span>{formatRelativeTime(record.createdAt)}</span>
				</div>
				<Button size="sm" variant="default" onClick={onViewDetails}>
					View Details
				</Button>
			</div>
		</div>
	);
};

export default FitCheckCard;
